/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Modal,
  Input,
  DatePicker,
  message,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError, onSuccess } from 'utils/handlers';
import { formatReceivedValues } from 'utils/formatValues';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
  ESTATUS_RECHAZADO,
} from 'utils/constants';
import {
  getLiquidacionesConfiguracionesLaudos,
  getLiquidacionConfiguracionLaudo,
  postLiquidacionConfiguracionLaudo,
  patchLiquidacionConfiguracionLaudo,
  deleteLiquidacionConfiguracionLaudo,
} from 'api/nominas/liquidaciones/liquidacionesConfiguracionesLaudos';

export const liquidacionConfiguracionLaudoPermission = {
  permissionModel: 'liquidacionconfiguracionlaudo',
  catalogsModels: [],
  tabsModels: [],
};

const LiquidacionConfiguracionLaudo = ({ permission, liquidacionConfiguracion }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState();
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [visibleAlert, setVisibleAlert] = useState();
  const [disableAuth, setDisableAuth] = useState(false);
  const [disableReactivar, setDisableReactivar] = useState(true);
  const [disableCancelar, setDisableCancelar] = useState(false);
  // Un registro Autorizado o Cancelado ya no debe poder editarse ni eliminarse.
  const [disableEditDelete, setDisableEditDelete] = useState(true);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [visibleReactivarConfirmation, setVisibleReactivarConfirmation] = useState(false);
  const [visibleCancelarConfirmation, setVisibleCancelarConfirmation] = useState(false);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesConfiguracionesLaudos();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getLiquidacionesConfiguracionesLaudos(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableAuth(false);
    setDisableReactivar(true);
    setDisableCancelar(false);
    setDisableEditDelete(true);
    form.resetFields();
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacionConfiguracionLaudo(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    if (!match) {
      setLoading(false);
      return;
    }
    // El doble clic en una fila llama a esta funcion directo, sin pasar por el
    // boton "Editar" del toolbar (ver onRow.onDoubleClick en components/Table.jsx).
    if (match.estados_globales === ESTATUS_AUTORIZADO || match.estados_globales === ESTATUS_CANCELADO) {
      setLoading(false);
      message.warn('No se puede editar un Laudo Autorizado o Cancelado.');
      return;
    }
    const liquidacionConfigLaudo = await getLiquidacionConfiguracionLaudo(match.id);
    setTimeout(() => {
      if (liquidacionConfigLaudo) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(liquidacionConfigLaudo));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchLiquidacionConfiguracionLaudo(key, values)
        : postLiquidacionConfiguracionLaudo(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un laudo ya autorizado');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchLiquidacionConfiguracionLaudo(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishReactivarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Reactivar un Laudo Activo');
        return;
      }
      // vigencia_final se limpia porque el backend fuerza Inactivo mientras tenga fecha
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_ACTIVO,
        vigencia_final: null,
      };
      const res = await patchLiquidacionConfiguracionLaudo(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleReactivarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishCancelarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar un laudo ya cancelado');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchLiquidacionConfiguracionLaudo(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleCancelarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    const isActivo = record?.estados_globales === ESTATUS_ACTIVO;
    const isCancelado = record?.estados_globales === ESTATUS_CANCELADO;
    const isRechazado = record?.estados_globales === ESTATUS_RECHAZADO;
    setDisableAuth(isAutorizado || isInactivo || isCancelado || isRechazado);
    setDisableReactivar(isActivo);
    setDisableCancelar(isCancelado);
    setDisableEditDelete(isAutorizado || isCancelado);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Laudo',
      dataIndex: 'laudo',
      key: 'laudo',
      width: 300,
      render: (val) => val?.toUpperCase(),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 200,
      render: (val) => (val ? val?.format?.(DATE_FRONTEND_FORMAT) ?? val : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableDelete={
            [ESTATUS_AUTORIZADO, ESTATUS_CANCELADO, ESTATUS_RECHAZADO].includes(
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales,
            )
          }
          disableEdit={!selectedRowKeys?.length || disableEditDelete}
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'liquidacionconfiguracionlaudo', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableAuth,
            },
            {
              onClick: () => setVisibleReactivarConfirmation(true),
              permission: { model: 'liquidacionconfiguracionlaudo', action: 'reactivar' },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableReactivar,
            },
            {
              onClick: () => setVisibleCancelarConfirmation(true),
              permission: { model: 'liquidacionconfiguracionlaudo', action: 'cancelar' },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length || disableCancelar,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={500}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Laudo`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col span={24}>
              <Form.Item
                name="laudo"
                label="Laudo"
                rules={rules.required}
                hasFeedback
              >
                <Input
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
                hasFeedback
              >
                <DatePicker
                  format={DATE_FRONTEND_FORMAT}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar el Laudo ${form.getFieldValue('laudo')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishReactivarConfirmation}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => {
            setVisibleReactivarConfirmation(false);
            onCancel();
          }}
          open={visibleReactivarConfirmation}
          contentTitle={`Reactivar el Laudo ${form.getFieldValue('laudo')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishCancelarConfirmation}
          ConfirmCancel
          onCancel={() => {
            setVisibleCancelarConfirmation(false);
            onCancel();
          }}
          open={visibleCancelarConfirmation}
          contentTitle={`Cancelar el Laudo ${form.getFieldValue('laudo')}`}
          loading={loading}
        />
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Laudo ${form.getFieldValue('laudo')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionConfiguracionLaudo;
